'use strict';

var Model = require('js/lib/model');
var _ = require('underscore');
var Collection = require('js/lib/collection');
var Members = require('js/collections/members');
var COMMON_SCHEMA = require('js/commonSchema');
var Api = require('js/api');
var config = require('js/config');
const formatModel = require('js/lib/formatModel');

import axios from 'axios';
import {getCookieValue} from 'js/utils/cookie';

/**
 * Post
 * @exports Post
 * @constructor
 * @alias module:models/post
 */
var Post = Model.extend({
  urlRoot: config.build.apiPath + '/api/post',
  urls: {
    blog: config.build.apiPath + '/api/blog', //remove synonimous or choose a convention to be consistent
    post: config.build.apiPath + '/api/blog',
    question: config.build.apiPath + '/api/question',
  },
  schema: {
    type: 'object',
    properties: {
      title: COMMON_SCHEMA.POST_TITLE,
      body: COMMON_SCHEMA.POST_BODY,
      groupCode: COMMON_SCHEMA.REQUIRED_STRING,
    },
  },
  postType: {
    post: 'HealthUnlocked.Aurora.DataModel.Content.Blog',
    blog: 'HealthUnlocked.Aurora.DataModel.Content.Blog',
    question: 'HealthUnlocked.Aurora.DataModel.Content.Question',
    poll: 'HealthUnlocked.Aurora.DataModel.Content.Poll',
  },
  dataCacheMap: {
    'users': Members,
  },
  defaults: {
    author: {},
  },
});

/**
 * Parse api endpoint data
 */
Post.prototype.parse = function(response) {
  var ratings;
  var userRatings;
  var attributes;

  // Ensure data cache
  this.ensureDataCache(response, this.dataCacheMap);
  const users = (this.getCachedData('users', {}, true) || [])
    .map(model => model.attributes);

  // Either got a single post or being created by a collection with raw attributes
  attributes = this.parseResponses(response.post || response, users);

  // Handle ratings
  if (ratings = response.ratings) {
    if (ratings[attributes.id]) {
      attributes.rating = parseInt(ratings[attributes.id]);
    }

    // Ratings on responses
    if (attributes.responses) {
      attributes.responses.each(function(post) {
        if (ratings[post.id]) {
          post.set('rating', parseInt(ratings[post.id]), {silent: true});
        }
      });
    }
  }

  // Handle user ratings
  if (userRatings = response.userRatings) {
    if (userRatings.indexOf(this.id) > -1) {
      attributes.hasRated = true;
    }

    // User ratings on responses
    if (attributes.responses) {
      userRatings.forEach(function(id) {
        if (attributes.responses.get(id)) {
          attributes.responses.get(id).set('hasRated', true, {silent: true});
        }
      });
    }
  }

  // Contain isFollowing within attributes
  attributes.isFollowing = response.isFollowing;

  return formatModel({ model: attributes, users });
};

/**
 * Model parse method which specifically targets responses on a post
 */
Post.prototype.parseResponses = function(attributes, users) {
  // Format responses into a collection of posts
  if (attributes.responses && !(attributes.responses instanceof Collection)) {
    attributes.responses = new Collection(
      attributes.responses.map(response => formatModel({ model: response, users })),
      { model: Post.Response, parent: this }
    );
  }

  // [BB-963] we're making sure 'responses' has a value in order to override the old 'responses' in case of a re-fetch
  if (attributes.responses === undefined) {
    attributes.responses = null;
  }

  return attributes;
};

/**
 * Return the post authors id
 */
Post.prototype.getAuthorId = function() {
  if (this.get('authorId')) {
    return this.get('authorId');
  }

  var author = this.get('author');

  if (author && !_.isEmpty(author)) {
    if (author.id || author.id === 0) {
      return author.id;
    }

    return author.userId;
  }

  return this.get('userId');
};

/**
 * Return the number of responses on a post
 */
Post.prototype.getNumResponses = function() {
  if (this.has('totalResponses')) {
    return this.get('totalResponses');
  }

  if (this.has('responses')) {
    return this.get('responses').length;
  }

  return 0;
};

/**
 * Save a post as a particular type ('post' or 'question')
 * @param  {String} type    'post' or 'question'
 * @param  {Object} data    Data to 'save' (set before request)
 * @param  {Object} options Options for the save request
 * @return {Promise}
 */
Post.prototype.saveAs = function(type, data, options) {
  options = _.extend({
    url: this.urls[type]
  }, options);

  return this.save(data, options);
};

/**
 * Override destroy method to add some extra params
 */
Post.prototype.delete = function() {
  //TODO: refactor to use toFragment
  if (!this.id) {
    throw new Error('Model must be initialised with an "id" attribute.');
  }

  // Finally call super with some extra options to pass through
  return Post.__super__.destroy.call(this, {
    url: this.urlRoot + '/' + this.id
  });
};

/**
 * Override destroy method to add some extra params
 */
Post.prototype.destroy = function() {
  //TODO: refactor to use toFragment
  if (!this.id) {
    throw new Error('Model must be initialised with an "id" attribute.');
  }

  var deleteUrl = this.urlRoot + '/' + this.id + '?groupCode=' + this.options.groupCode + '&parentType=' + this.options.parentType;

  // Finally call super with some extra options to pass through
  return Post.__super__.destroy.call(this, {
    url: deleteUrl,
  });
};

/**
 * Delete a response of this post
 */
Post.prototype.deleteResponse = function(id) {
  return Api.delete(this.urlRoot + '/' + id + '?groupCode=' + this.get('groupCode') + '&superParentId=' + this.id + '&parentType=posts')
    .then(function() {
      this.fetch();
    }, this);
};

/**
 * Fetch a post by ID (and some other options)
 */
Post.prototype.fetchOne = function(id) {
  this.desc('id', id).fetch();
};

/**
 * Post a response
 * @return {} default props for responses send to api
 */
Post.prototype.getResponseCommonBody = function() {
  const context = app.context.getContext();
  return {
    body: this.get('body'),
    superParentId: this.id,
    groupCode: this.get('groupCode'),
    username: context.user.username,
    parentType: 'posts',
  };
};

/**
 * Post a response
 * @param  {String} body     Body string
 * @param  {Int} id       Direct post id
 * @param  {String} username Username of poster
 * @return {Promise}          Api promise
 */
Post.prototype.respond = function(data) {
  return Api.post(this.urlRoot + '/' + data.parentId + '/respond', {
    data: Object.assign(this.getResponseCommonBody(), data),
  })
    .then(() => this.fetch());
};

/**
 * Edit a response on this post
 */
Post.prototype.editResponse = function(data, {postUrl, ...options} = {}) {
  return Api.put(postUrl || this.urlRoot, {
    data: Object.assign(this.getResponseCommonBody(), data, options),
  })
  .then(() => this.fetch());
};

/**
 * Rate a post (toggles)
 * @return {Promise}          Api promise
 */
Post.prototype.rate = function(post, errCallback) {
  const hasRated = post.get('hasRated');
  const rating = post.get('rating') || 0;

  // Optimistically set rating
  post.set({
    hasRated: !hasRated,
    rating: hasRated ? rating - 1 : rating + 1,
  });

  return Api.post(this.urlRoot + '/' + post.id + '/rate', {
    data: {
      op: hasRated ? '-' : '+',
      id: post.id,
      superParentId: this.id,
      groupCode: this.get('groupCode'),
      parentType: 'posts',
    }
  }).then(
    () => {},
    () => {
      post.set({
        hasRated: hasRated,
        rating: rating,
      });
      if (errCallback) {
        errCallback();
      }
    }
  );
};

/**
 * Pin a post
 * @return {Promise}          Api promise
 */
Post.prototype.pin = function(id, groupCode) {
  id = arguments.length ? arguments[0] : this.id;
  groupCode = arguments.length === 2 ? arguments[1] : this.get('groupCode');

  //IMPORTANT  to refer to Post.prototype.urlRoot rather than to the alias endpoint for polls and questions
  return Api.post(Post.prototype.urlRoot + '/' + id + '/pin',
    {
      data: {
        id: id,
        groupCode: groupCode,
      },
    }
  ).then(function(response) {
    this.set('isPinned', response && response.isPinned);
  }, this);

};

/**
 * Fetch autotags for current title and body
 */
Post.prototype.getTags = function() {
  return axios.post(config.build.classifierApiPath + '/autotag', {
    title: this.get('title'),
    body: this.get('body').replace(config.regex.BBCODE, ''),
    groupId: this.get('groupId') || app.context.getGroup().groupId,
    lang: getCookieValue('huLang') || 'en',
  }).then(response => _.filter(response.data.entities, e => e.isDisplayed));
};

/**
 * Report this post
 */
Post.prototype.report = function(reason, comment) {
  return Api.post(Post.prototype.urlRoot + '/' + this.id + '/report', {
    data: {
      reason: reason,
      comment: comment,
      id: this.id,
      groupCode: this.get('groupCode'),
      parentType: 'posts',
    },
  });
};

/**
 * Upload or remove existing image associated with the post
 */
Post.prototype.updateImage = function(Base64Image) {
  var data = {Base64Image: Base64Image};

  if (this.id) {
    data.postId = this.id;
  }

  return Api.post(Post.prototype.urlRoot + '/image', {data: data});
};

Post.prototype.lock = function() {
  return Api.put(`${Post.prototype.urlRoot}/${this.id}/lock`,
    {
      data: {
        isLocked: true,
        groupCode: this.get('groupCode'),
      },
    }
  ).then(() => this.set('isLocked', true));
};

Post.prototype.unlock = function() {
  return Api.put(`${Post.prototype.urlRoot}/${this.id}/lock`,
    {
      data: {
        isLocked: false,
        groupCode: this.get('groupCode'),
      },
    }
  ).then(() => this.set('isLocked', false));
};


/**
 * Response type of post
 */
Post.Response = Post.extend({
  schema: {
    type: 'object',
    properties: {
      body: COMMON_SCHEMA.POST_BODY,
    },
  },
});

module.exports = Post;



// WEBPACK FOOTER //
// ./app/assets/js/models/post.js